import {
  ActionIcon,
  Flex,
  Group,
  Indicator,
  MultiSelect,
  Popover,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconFilter } from "@tabler/icons-react";
import CreatableSelect from "components/ui/CreatableSelect/CreatableSelect";
import ExportExcelAction from "components/ui/ExportExcelAction/ExportExcelAction";
import { SearchInput } from "components/ui/SearchInput/SearchInput";
import { DEBT_STATUS_LABEL } from "constants/common/debt";
import { ExportModule } from "constants/common/exportJob";
import { DebtParamsType, DebtStatus } from "constants/types/debt";
import { FC, useMemo } from "react";
import { usePopoverClickOutside } from "utils/hooks/ui/usePopoverClickOutside";
import { useCustomers } from "utils/hooks/request/usePartners";
import { useUsers } from "utils/hooks/request/useUsers";

const STATUS_OPTIONS = Object.entries(DEBT_STATUS_LABEL).map(
  ([value, label]) => ({ value, label }),
);

type DebtActionsProps = {
  params: DebtParamsType;
  onSearch: (value: string) => void;
  onFilterChange: (filters: Partial<DebtParamsType>) => void;
};

const DebtActions: FC<DebtActionsProps> = ({
  params,
  onSearch,
  onFilterChange,
}) => {
  const [filterOpened, { close: closeFilter, toggle: toggleFilter }] =
    useDisclosure(false);
  const { triggerRef, dropdownRef } = usePopoverClickOutside(
    filterOpened,
    closeFilter,
  );

  const { customerOptions, isLoading: customersLoading } = useCustomers();
  const { userOptions, isLoading: usersLoading } = useUsers();

  const activeFilterCount = useMemo(
    () =>
      [
        params.partnerId,
        params.contactPersonId,
        params.status?.length ? params.status : undefined,
      ].filter(Boolean).length,
    [params.partnerId, params.contactPersonId, params.status],
  );

  return (
    <Flex justify="space-between" align="center" gap="sm">
      <Group gap="sm">
        <SearchInput
          value={params.search || ""}
          onChange={onSearch}
          placeholder="Tìm kiếm công nợ / Đơn BH"
        />
        <Popover
          opened={filterOpened}
          onClose={closeFilter}
          position="bottom-start"
          shadow="md"
          withinPortal
          closeOnClickOutside={false}
        >
          <Popover.Target>
            <Indicator
              label={activeFilterCount}
              size={16}
              disabled={activeFilterCount === 0}
              color="blue"
            >
              <ActionIcon
                ref={triggerRef}
                variant="default"
                size="lg"
                onClick={toggleFilter}
                aria-label="Bộ lọc"
              >
                <IconFilter size={18} />
              </ActionIcon>
            </Indicator>
          </Popover.Target>

          <Popover.Dropdown>
            <div ref={dropdownRef}>
              <Stack gap="sm" w={280}>
                <Text size="sm" fw={600}>
                  Bộ lọc
                </Text>
                <CreatableSelect
                  label="Khách hàng"
                  placeholder="Tất cả"
                  data={customerOptions ?? []}
                  value={params.partnerId ?? null}
                  onChange={(v) =>
                    onFilterChange({ partnerId: v ?? undefined })
                  }
                  allowCreate={false}
                  clearable
                  disabled={customersLoading}
                  size="sm"
                />
                <MultiSelect
                  label="Trạng thái"
                  placeholder="Tất cả"
                  data={STATUS_OPTIONS}
                  value={params.status ?? []}
                  onChange={(v) =>
                    onFilterChange({
                      status: v.length > 0 ? (v as DebtStatus[]) : undefined,
                    })
                  }
                  clearable
                  size="sm"
                  styles={{
                    pillsList: { flexWrap: "nowrap", overflow: "hidden" },
                  }}
                />
                <CreatableSelect
                  label="Người phụ trách"
                  placeholder="Tất cả"
                  data={userOptions ?? []}
                  value={params.contactPersonId ?? null}
                  onChange={(v) =>
                    onFilterChange({ contactPersonId: v ?? undefined })
                  }
                  allowCreate={false}
                  clearable
                  disabled={usersLoading}
                  size="sm"
                />
              </Stack>
            </div>
          </Popover.Dropdown>
        </Popover>
      </Group>

      <ExportExcelAction
        module={ExportModule.DEBT}
        permission="debt:export"
        // Bộ lọc đang đặt trên màn danh sách, áp cho tab "Tuỳ chọn" của hộp
        // thoại xuất. Công nợ không có cột nhà kho nên không truyền
        // warehouseId — backend cũng khai module này không hỗ trợ lọc đó.
        filters={{
          partnerId: params.partnerId,
          status: params.status,
        }}
      />
    </Flex>
  );
};

export default DebtActions;
